import { useEffect, useState } from 'react';
import { isValidId, updateActiveTask } from '../../util/Util';
import { useBom } from './BomContext';
import { useUI } from '../../providers/MoleUIContext';
import { useAuth } from '../../hooks/useAuth';
import RefreshButton from '../../components/RefreshButton';
import {
	Autocomplete,
	Checkbox,
	FormControlLabel,
	Grid,
	TextField,
} from '@mui/material';
import { MYSQL_LIMITS } from '../../util/constants';

interface Props {
	setValidateCheck: (value: boolean) => void;
	readOnly?: boolean;
}

const BomDetail = ({ setValidateCheck, readOnly = false }: Props) => {
	const { setPendingTasks, activeTask, setItems } = useBom();
	const { t, queueReRun } = useUI();
	const { api } = useAuth();
	const [depots, setDepots] = useState<any[]>([]);
	const [isDepotLoading, setIsDepotLoading] = useState(false);

	const fetchDepots = async () => {
		setIsDepotLoading(true);
		const res = await api.get('/depot/meterred');
		if (res?.status === 200) {
			setDepots(res.data || []);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchDepots);
		}
		setIsDepotLoading(false);
	};

	useEffect(() => {
		fetchDepots();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const ok =
			!!(activeTask?.name && activeTask.name.trim().length > 0) &&
			isValidId(depots, activeTask?.depot);
		setValidateCheck(ok);
	}, [activeTask?.name, activeTask?.depot, depots, setValidateCheck]);

	const onDepotChanged = (depotId: number) => {
		// Changing depot clears lines — items are scoped to that depot
		updateActiveTask(setPendingTasks, {
			depot: depotId,
			items: [],
		});
		setItems([]);
	};

	return (
		<Grid container spacing={0}>
			<Grid size={{ xs: 6, sm: 6, md: 3 }}>
				<Autocomplete
					size="small"
					options={depots}
					loading={isDepotLoading}
					disabled={!activeTask || readOnly || isDepotLoading}
					getOptionLabel={(o) => o.depot_name || o.DepotName || o.name || ''}
					value={depots.find((d) => d.id === activeTask?.depot) || null}
					onChange={(_, v) => onDepotChanged(v?.id ?? -1)}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="filled"
							required
							fullWidth
							label={
								<span>
									{t('common:production.productionDepot')}&nbsp;&nbsp;
									<RefreshButton
										isWorking={isDepotLoading}
										onClick={fetchDepots}
										disabled={isDepotLoading || readOnly}
									/>
								</span>
							}
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 6, md: 4 }}>
				<TextField
					size="small"
					fullWidth
					variant="filled"
					required
					label={t('common:production.bomName')}
					value={activeTask?.name || ''}
					disabled={!activeTask || readOnly}
					inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(100).maxLength }}
					onChange={(e) =>
						updateActiveTask(setPendingTasks, { name: e.target.value })
					}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 6, md: 3 }}>
				<TextField
					size="small"
					fullWidth
					variant="filled"
					label={t('common:production.bomCode')}
					value={activeTask?.code || ''}
					disabled={!activeTask || readOnly}
					inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
					onChange={(e) =>
						updateActiveTask(setPendingTasks, { code: e.target.value })
					}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 6, md: 2 }}>
				<FormControlLabel
					control={
						<Checkbox
							checked={activeTask?.is_active ?? true}
							disabled={!activeTask || readOnly}
							onChange={(e) =>
								updateActiveTask(setPendingTasks, {
									is_active: e.target.checked,
								})
							}
						/>
					}
					label={t('common:status.active')}
				/>
			</Grid>
		</Grid>
	);
};

export default BomDetail;
